import React, { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import i18n from 'i18n-js';
import * as Yup from 'yup';
import { buildUrl } from 'utils';
import Title from 'components/Title';
import Layout from 'components/Layout';
import { Formik, Field } from 'formik';
import { useParams, useHistory } from 'react-router-dom';
import { TextField } from 'components/FormFields';
import { useDispatch, useSelector } from 'react-redux';
import { parseBusinessHoursData } from 'services/rules';
import { BusinessHoursTable } from 'components/BusinessHours';
import { BusinessHoursActions } from 'state/modules/businessHours';

const BusinessHoursCreate = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const formRef = useRef(null);
  const { id, action } = useParams();
  const {
    loading,
    currentId,
    current: { name, bh_rules, description, error_message },
    fetchedCreateBH,
    fetchedOverwriteBH,
  } = useSelector((state) => state.businessHours);

  const initialValues = {
    bh_rules: bh_rules || [],
    description: description || '',
    error_message: error_message || '',
    name: action === 'clone' ? i18n.t('common.copy_of', { name }) : name || '',
  };

  const formSchema = Yup.object().shape({
    error_message: Yup.string().required(i18n.t('common.required_field')),
    description: Yup.string().required(i18n.t('common.required_field')),
    name: Yup.string().required(i18n.t('common.required_field')),
    bh_rules: Yup.array(),
  });

  const onSubmitHours = (bh) => {
    dispatch(
      BusinessHoursActions.updateCurrentBH({
        bh_rules: bh,
        name: formRef.current.values.name,
        description: formRef.current.values.description,
        error_message: formRef.current.values.error_message,
      })
    );

    formRef.current.setFieldValue('bh_rules', bh);
  };

  const onSubmit = (values) => {
    if (action === 'update' && id) {
      dispatch(BusinessHoursActions.fetchOverwriteBH(id, values));
    } else {
      dispatch(BusinessHoursActions.fetchCreateBH(values));
    }
  };

  const onDelete = (row) => dispatch(BusinessHoursActions.handleDeleteBusinessHours(row));

  useEffect(() => {
    if ((fetchedCreateBH || fetchedOverwriteBH) && currentId) {
      history.push(buildUrl(`/business-hours/${currentId}`));
      dispatch(BusinessHoursActions.resetCurrentBusinessHour());
    }
  }, [dispatch, history, fetchedCreateBH, fetchedOverwriteBH, currentId]);

  useEffect(() => {
    if (id) {
      dispatch(BusinessHoursActions.fetchBusinessHourById(id));
    }

    return () => {
      dispatch(BusinessHoursActions.resetCurrentBusinessHour());
    };
  }, [id, dispatch]);

  return (
    <Layout title={i18n.t('business_hours.title')}>
      <Container sx={{ minWidth: '100%', margin: 0 }}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <Grid
              container
              spacing={2}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid>
                <Title title={i18n.t(`business_hours.${action || 'create'}`)} />
              </Grid>
              <Grid>
                <Grid container spacing={2} direction="row" alignItems="center">
                  <Grid>
                    <Button
                      data-cy="btn-cancel"
                      color="primary"
                      disabled={loading}
                      variant="outlined"
                      style={{ textTransform: 'capitalize' }}
                      onClick={() => history.push(buildUrl(`/business-hours`))}
                    >
                      {i18n.t('common.cancel')}
                    </Button>
                  </Grid>
                  <Grid>
                    <Button
                      data-cy="btn-save"
                      color="primary"
                      disabled={loading}
                      variant="contained"
                      style={{ textTransform: 'capitalize' }}
                      onClick={() => formRef.current.submitForm()}
                    >
                      {i18n.t('common.save')}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Grid size={12}>
              <Paper
                elevation={2}
                sx={{ boxShadow: '0px 3px 6px #0000001A', padding: '32px 48px', marginTop: '15px' }}
              >
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <Typography align="left">
                      <Box mt={1} fontSize={14} component="span" fontWeight="medium">
                        {i18n.t('business_hours.subtitle')}
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid size={12}>
                    <Formik
                      initialValues={initialValues}
                      validationSchema={formSchema}
                      enableReinitialize
                      innerRef={formRef}
                      onSubmit={onSubmit}
                    >
                      {({ handleSubmit }) => (
                        <form noValidate onSubmit={handleSubmit}>
                          <Grid container spacing={2} direction="row">
                            <Grid size={{ xs: 12, sm: 4 }}>
                              <Field
                                name="name"
                                margin="none"
                                component={TextField}
                                inputProps={{ autoComplete: 'nope' }}
                                label={i18n.t('business_hours.ruleset_name')}
                              />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                              <Field
                                margin="none"
                                name="description"
                                component={TextField}
                                label={i18n.t('common.description')}
                                inputProps={{ autoComplete: 'nope' }}
                              />
                            </Grid>
                            <Grid size={12}>
                              <Typography align="left">
                                <Box mt={1} fontSize={14} component="span" fontWeight="medium">
                                  {i18n.t('business_hours.error_msg_subtitle_1')}
                                </Box>
                              </Typography>
                            </Grid>
                            <Grid size={12}>
                              <Typography align="left">
                                <Box mt={1} fontSize={14} component="span" fontWeight="medium">
                                  {i18n.t('business_hours.error_msg_subtitle_2')}
                                </Box>
                              </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 10 }}>
                              <Field
                                required
                                margin="none"
                                name="error_message"
                                component={TextField}
                                inputProps={{ autoComplete: 'nope' }}
                                label={i18n.t('business_hours.error_msg_placeholder')}
                              />
                            </Grid>
                          </Grid>
                        </form>
                      )}
                    </Formik>
                  </Grid>
                  <Grid size={12} style={{ marginTop: 25 }}>
                    <BusinessHoursTable
                      id={id}
                      action={action}
                      rules={bh_rules}
                      onDelete={onDelete}
                      onSubmitHours={onSubmitHours}
                      bh={parseBusinessHoursData(bh_rules)}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default BusinessHoursCreate;
